import type {RefObject} from 'react'
import {useEffect, useState} from 'react'

// Leaves room for the collapsed drawer, so the headline figures count as visible only once they clear it
const COLLAPSED_DRAWER_HEIGHT_PX = 90

const isOnScreen = (element: HTMLElement): boolean => {
  const {top, bottom} = element.getBoundingClientRect()

  return top < window.innerHeight && bottom > 0
}

const isFullyAboveDrawer = (element: HTMLElement): boolean => {
  const {top, bottom} = element.getBoundingClientRect()

  return top >= 0 && bottom <= window.innerHeight - COLLAPSED_DRAWER_HEIGHT_PX
}

export const useResultsDrawerVisibility = (
  controlsRef: RefObject<HTMLElement>,
  headlineRef: RefObject<HTMLElement>,
): boolean => {
  const [isVisible, setIsVisible] = useState(false)

  // Show the drawer while the calculator controls are on screen, and hide it once the headline savings figures are readable
  useEffect(() => {
    const controls = controlsRef.current
    const headline = headlineRef.current

    if (!controls || !headline) {
      return undefined
    }

    let frame = 0

    const update = () => {
      cancelAnimationFrame(frame)

      frame = requestAnimationFrame(() => setIsVisible(isOnScreen(controls) && !isFullyAboveDrawer(headline)))
    }

    update()
    window.addEventListener('scroll', update, {passive: true})
    window.addEventListener('resize', update)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [controlsRef, headlineRef])

  return isVisible
}
